import type {
  UfdrArtifactStat,
  UfdrCardItem,
  UfdrChatMessage,
  UfdrChatSummary,
  UfdrContact,
  UfdrFacet,
  UfdrGalleryItem,
  UfdrRecord,
  UfdrSearchGroup,
  UfdrTimelineEvent,
} from '@/shared/ufdr/legacy-types';
import type { MapViewerData } from '@tranquility-ai/theme-components/map-viewer';
import type {
  MobileAcquisitionStat,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import type { CardItem } from '@/shared/mobile-service/lib/build-cards-query';
import type { FacetResult } from '@/shared/mobile-service/lib/build-facet-query';
import type { GalleryItem } from '@/shared/mobile-service/lib/build-gallery-query';
import type { SearchGroup } from '@/shared/mobile-service/lib/build-search-query';
import type { TimelineEvent } from '@/shared/mobile-service/lib/build-timeline-query';
import type { ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import { artifactBubbleView } from '@/shared/mobile-service/lib/labels';
import type { MapPoint } from '@/shared/mobile-service/lib/map-points';
import type { MappedMessage, MessageThread } from '@/shared/mobile-service/lib/message-mapping';
import type { FilterState } from '@/shared/mobile-service/lib/where';
import { toSearchablePath } from '@/shared/functions';

/** Separates a GraphQL type from a thread key inside a chat id. */
export const UFDR_CHAT_ID_SEPARATOR = '\u0001';

/**
 * Searchable path sent to mobile-service. `s3://` URLs are not acquisition
 * paths and are rejected so they cannot pin the wrong phone.
 */
export function ufdrSearchableScope(path: string | null | undefined): string | null {
  const searchable = toSearchablePath(path ?? '').trim();
  if (!searchable || searchable.startsWith('s3://')) return null;
  return searchable;
}

const NAME_FIELDS = ['name', 'displayname', 'contactname', 'fullname', 'title'];
const PHONE_FIELDS = ['phonenumber', 'phone', 'phonenumbers', 'msisdn', 'number'];
const EMAIL_FIELDS = ['email', 'emailaddress', 'emails'];

export function ufdrChatId(graphqlType: string, threadKey: string): string {
  return `${graphqlType}${UFDR_CHAT_ID_SEPARATOR}${threadKey}`;
}

/**
 * Chat id for a `/mobile-viewer` thread.
 * Citation links store the raw thread key (`threadId=44`) next to `type`.
 * Picking a thread in the viewer stores the composite chat id. Both open
 * the same conversation.
 */
export function openChatIdFromRoute(
  threadId: string | null | undefined,
  artifactType: string | null | undefined,
): string | null {
  const raw = threadId?.trim();
  if (!raw) return null;
  if (splitUfdrChatId(raw)) return raw;
  const type = artifactType?.trim();
  if (!type) return null;
  return ufdrChatId(type, raw);
}

export function splitUfdrChatId(
  id: string | null | undefined,
): { graphqlType: string; threadKey: string } | null {
  if (!id) return null;
  const index = id.indexOf(UFDR_CHAT_ID_SEPARATOR);
  if (index <= 0) return null;
  const threadKey = id.slice(index + UFDR_CHAT_ID_SEPARATOR.length);
  if (!threadKey) return null;
  return { graphqlType: id.slice(0, index), threadKey };
}

export function isContactArtifact(artifact: MobileUiConfigArtifact): boolean {
  if (artifact.front_end?.meta?.family === 'contact') return true;
  const haystack = [artifact.category, artifact.collection, artifact.title, artifact.graphql_type]
    .filter(Boolean)
    .join(' ');
  return /contact/i.test(haystack);
}

export function messageArtifacts(
  uiConfig: MobileUiConfigResponse | undefined,
): MobileUiConfigArtifact[] {
  if (!uiConfig) return [];
  return Object.values(uiConfig.artifacts).filter((artifact) => artifactBubbleView(artifact));
}

export function contactArtifacts(
  uiConfig: MobileUiConfigResponse | undefined,
): MobileUiConfigArtifact[] {
  if (!uiConfig) return [];
  return Object.values(uiConfig.artifacts).filter(isContactArtifact);
}

/** Prefer the artifact that the acquisition summary says has the most rows. */
export function preferByCount(
  artifacts: MobileUiConfigArtifact[],
  stats: MobileAcquisitionStat[] | undefined,
): MobileUiConfigArtifact | undefined {
  if (artifacts.length === 0) return undefined;
  const counts = new Map((stats ?? []).map((stat) => [stat.graphqlType, stat.count]));
  return [...artifacts].sort(
    (left, right) => (counts.get(right.graphql_type) ?? 0) - (counts.get(left.graphql_type) ?? 0),
  )[0];
}

export function mapAcquisitionStats(
  stats: MobileAcquisitionStat[] | undefined,
  uiConfig: MobileUiConfigResponse | undefined,
): UfdrArtifactStat[] {
  return (stats ?? [])
    .filter((stat) => stat.count > 0)
    .map((stat) => {
      const artifact = uiConfig?.artifacts[stat.graphqlType];
      const category = artifact?.category || artifact?.front_end?.meta?.family || undefined;
      return {
        id: stat.graphqlType,
        label: artifact?.title || stat.artifactName || stat.graphqlType,
        category: category || undefined,
        count: stat.count,
      };
    });
}

export function mapThreadsToChats(
  threads: MessageThread[],
  app: string,
  graphqlType: string,
): UfdrChatSummary[] {
  return threads.map((thread) => ({
    id: ufdrChatId(graphqlType, thread.key),
    app,
    peer: thread.title,
    lastMessage: thread.subtitle ?? undefined,
    timestamp: thread.lastActivity ?? undefined,
    messageCount: thread.count,
  }));
}

export function mapMappedMessages(messages: MappedMessage[]): UfdrChatMessage[] {
  return messages.map((message) => ({
    id: message.id,
    direction: message.direction,
    text: message.body || undefined,
    timestamp: message.timestamp ?? undefined,
    sender: message.senderLabel ?? undefined,
    attachments:
      message.attachments.length > 0
        ? message.attachments.map((attachment) => ({
            type:
              attachment.kind === 'image' || attachment.kind === 'video'
                ? attachment.kind
                : 'link',
            url: attachment.url,
            alt: attachment.filename ?? undefined,
          }))
        : undefined,
  }));
}

export function attachChatMessages(
  chats: UfdrChatSummary[] | undefined,
  openChatId: string | null,
  messages: UfdrChatMessage[] | undefined,
): UfdrChatSummary[] | undefined {
  if (!chats || !openChatId || !messages) return chats;
  return chats.map((chat) =>
    chat.id === openChatId
      ? { ...chat, messages, messageCount: messages.length || chat.messageCount }
      : chat,
  );
}

function lookupField(row: Record<string, unknown>, names: string[]): unknown {
  for (const key of Object.keys(row)) {
    if (names.includes(key.toLowerCase())) return row[key];
  }
  return undefined;
}

function asTextList(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  if (typeof value === 'number' && Number.isFinite(value)) return [String(value)];
  if (Array.isArray(value)) return value.flatMap(asTextList);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return asTextList(
      record.phoneNumber ?? record.phone ?? record.email ?? record.name ?? record.value ?? record.identifier,
    );
  }
  return [];
}

export function mapContactRows(rows: Array<Record<string, unknown>>): UfdrContact[] {
  return rows.flatMap((row, index) => {
    const name = asTextList(lookupField(row, NAME_FIELDS))[0];
    const phoneNumbers = asTextList(lookupField(row, PHONE_FIELDS));
    const emails = asTextList(lookupField(row, EMAIL_FIELDS));
    if (!name && phoneNumbers.length === 0 && emails.length === 0) return [];
    const label = name || phoneNumbers[0] || emails[0] || 'Unknown';
    const id = String(row.mobileArtifactId ?? row.id ?? `${label}-${index}`);
    return [
      {
        id,
        name: label,
        phoneNumbers: phoneNumbers.length > 0 ? phoneNumbers : undefined,
        emails: emails.length > 0 ? emails : undefined,
      },
    ];
  });
}

export function mapMobileTimeline(events: TimelineEvent[]): UfdrTimelineEvent[] {
  return events.map((event) => ({
    id: event.id,
    timestamp: event.timestamp,
    description: event.summary ? `${event.title}: ${event.summary}` : event.title,
    category: event.typeLabel || event.graphqlType,
  }));
}

export function mapGalleryItems(items: GalleryItem[]): UfdrGalleryItem[] {
  return items.map((item) => ({
    id: item.id,
    label: item.caption || item.id,
    mediaType: item.kind === 'document' ? 'file' : item.kind,
    src: item.url,
    thumbnailUrl: item.thumbnailUrl || undefined,
    timestamp: item.timestamp ?? undefined,
    broken: !item.url,
  }));
}

export function mapCardItems(items: CardItem[]): UfdrCardItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    subtitle: item.subtitle ?? undefined,
    fields: item.details.map((detail) => ({ label: detail.label, value: detail.value })),
  }));
}

export function mapPointsToMapData(points: MapPoint[], legendLabel: string): MapViewerData {
  return {
    markers: points.map((point) => ({
      id: point.id,
      coordinate: [point.longitude, point.latitude],
      title: point.label ?? undefined,
      timestamp: point.timestamp ?? undefined,
      legendId: 'locations',
    })),
    legend: [
      {
        id: 'locations',
        label: legendLabel,
        color: 'var(--color-chart-1)',
        count: points.length,
      },
    ],
  };
}

export function mapFacetResult(
  result: FacetResult | undefined,
  filters: FilterState,
): UfdrFacet[] {
  return (result?.groups ?? []).map((group) => {
    const selected = filters[group.field];
    return {
      id: group.field,
      label: group.label,
      options: group.values.map((value) => ({
        value: value.value,
        label: value.label,
        count: value.count,
      })),
      selected: selected?.kind === 'enum' ? selected.values : undefined,
    };
  });
}

export function mapSearchGroups(groups: SearchGroup[] | undefined): UfdrSearchGroup[] {
  return (groups ?? []).map((group) => ({
    artifactTypeId: group.graphqlType,
    label: group.typeLabel,
    hits: group.hits.map((hit) => ({
      id: hit.id,
      title: hit.snippet || hit.id,
      subtitle: hit.field ?? undefined,
    })),
  }));
}

export function mapArtifactRecord(args: {
  id: string;
  row: Record<string, unknown> | null | undefined;
  columns: ResolvedColumn[];
  title?: string;
}): UfdrRecord | undefined {
  if (!args.row) return undefined;
  const fields = args.columns.flatMap((column) => {
    const value = args.row?.[column.field];
    if (value == null || value === '') return [];
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return [{ label: column.label, value: text }];
  });
  return {
    id: args.id,
    title: args.title,
    fields,
  };
}

export function mapResolvedColumns(columns: ResolvedColumn[]): Array<{
  id: string;
  label: string;
  filter?: 'text' | 'enum' | 'number' | 'datetime';
  enumOptions?: string[];
}> {
  return columns.map((column) => ({
    id: column.field,
    label: column.label,
    filter:
      column.filter === 'string' || column.filter === 'boolean'
        ? 'text'
        : column.filter === 'enum' ||
            column.filter === 'number' ||
            column.filter === 'datetime'
          ? column.filter
          : undefined,
    enumOptions: column.enumValues,
  }));
}
